import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  graphql, Script, ScriptStrategy,
} from 'gatsby';
import { useLocation } from '@gatsbyjs/reach-router';
import { useSegmentation } from '@printdeal/segmentation/react';
import { useIntl } from 'react-intl';
import Layout from '../../components/Layout';
import './home.css';
import ScrollCue from '../../components/homepage/scroll-cue/ScrollCue';
import { addSearchBoxStructuredData } from '../../helpers/StructuredData/SearchBox';
import { findNodeByLocale, filterNodesByLocale } from '../../helpers/translations';
import SEO from '../../components/Seo';

// Events
import {
  handleOnScrollCueClick,
} from '../../components/homepage/Homepage.events';

// Homepage Sections
import SectionExperiment from '../../components/homepage/homepage-sections/SectionExperiment';

// Helpers
import {
  getDataFromSections,
  scrollToRef,
} from '../../components/homepage/helpers';

// Types
import type {
  HomePageProps,
  SectionNodeProps,
  HomepageSectionProps,
  SystemPageSeoProps,
  HomeProps,
} from '../../components/homepage/types';

import {
  AudiencesData,
  getAudiences,
  getHomepageDataForAudience,
  getQueryParamFromLocation,
} from './homepage-helper';
import { getFromLocalStorage, LS_KEYS } from '../../helpers/window';

export const query = (
  graphql`
  query Homepage {
    homepages: allContentfulHomepage(filter: {type: {eq: "release"}}) {
        nodes {
          contentful_id
          node_locale
          audienceType
          sections {
            ... on ContentfulExperiment {
              id
              experimentId
              control {
                ...HomepageSectionFragment
              }
              variant {
                ...HomepageSectionFragment
              }
            }
            ...HomepageSectionFragment
          }
        }
      },
      portalConfiguration {
        channel {
          name
          siteUrl
        }
      },
    }
  `
);

const HomePage = ({
  data,
  pageContext,
}: HomePageProps) => {
  const {
    homepages,
    portalConfiguration,
  } = data;
  const intl = useIntl();
  const location = useLocation();
  const { data: traits, isSuccess: isAudienceLoadedFromSegment } = useSegmentation();
  const audienceOverRideForPlayground = getQueryParamFromLocation(location, 'variant');
  /**
   * Memoizing audience data
   */
  const audiencesDataFromSegment: AudiencesData = useMemo(
    () => getAudiences(traits, isAudienceLoadedFromSegment, audienceOverRideForPlayground),
    [isAudienceLoadedFromSegment, traits, audienceOverRideForPlayground],
  );

  const emulatedAudienceType = audienceOverRideForPlayground;
  /**
   * The value of Audience type either set by emulatedAudienceType for playground or by LS AUDIENCE_TYPE or traits
   */
  const customerAudienceType = emulatedAudienceType || getFromLocalStorage(LS_KEYS.AUDIENCE_TYPE)
    || audiencesDataFromSegment?.type;

  // Scrolly code
  const [showScrolly, setShowScrolly] = useState<boolean | undefined>(undefined);
  const [scrollCuesInView, setScrollCuesInView] = useState<boolean>(false);
  const handleScroll = useCallback(() => {
    if (window.scrollY > 20) {
      setShowScrolly(false);
    }
  }, []);
  useEffect(() => {
    setShowScrolly(true);
    document.body.addEventListener('touchmove', handleScroll);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Fetch all the Sections added in Homepage contentful model for rendering.
  const allHomepages = useMemo(
    () => filterNodesByLocale(homepages?.nodes, intl.locale) as HomeProps[],
    [homepages?.nodes, intl.locale],
  );

  const { sections } = getHomepageDataForAudience(customerAudienceType, allHomepages) as HomeProps;

  // Fetch homepage path and nodeLocale from pageContent for Post script Hydration.
  const pageContent = useMemo(
    () => getDataFromSections(sections as HomepageSectionProps[], 'seo_section', 'seo_information') as SystemPageSeoProps,
    [sections],
  );

  // Variable is used to pass Homepage path depending upon Locale
  const { path: pathHome, node_locale: nodeLocale } = pageContent || {};

  const jsonLD = addSearchBoxStructuredData(portalConfiguration.channel.siteUrl, pathHome, nodeLocale);

  // Refs to point to the section nodes
  const popularProductSectionNode = useRef<HTMLDivElement | null>(null);

  return (
    <>
      {/* Google Merchant Center needs to crawl to provide quick way for people to search your site or
      app immediately on the search results page */}
      <Script type="application/ld+json" strategy={ScriptStrategy.postHydrate}>
        {JSON.stringify(jsonLD)}
      </Script>

      <Layout localizedPaths={pageContext.localizedPaths}>
        {/* Generate all sections */}
        {sections?.length !== 0 && (
          sections?.map((section) => (
            <SectionExperiment
              key={`${section.id}`}
              section={section}
              audiencesDataFromSegment={audiencesDataFromSegment}
              scrollCuesInView={scrollCuesInView}
              onCueClick={() => {
                scrollToRef(popularProductSectionNode);
              }}
              setScrollCuesInView={setScrollCuesInView}
              popularProductSectionNode={popularProductSectionNode}
            />
          ))
        )}

        {/* Scrolly teaser pops-up only if a fixed scrolly is not in view. */}
        {!scrollCuesInView && showScrolly && (
        <ScrollCue
          className="scrolly-teaser"
          testId="teaser-arrow-icon"
          onCueClick={() => {
            scrollToRef(popularProductSectionNode);
            handleOnScrollCueClick('popular_products');
          }}
        />
        )}
      </Layout>
    </>
  );
};

export const Head = ({ data: { homepages }, pageContext }: HomePageProps) => {
  const { sections }: SectionNodeProps = findNodeByLocale(homepages?.nodes, pageContext.locale) || {};
  const pageContent = getDataFromSections(sections as HomepageSectionProps[], 'seo_section', 'seo_information') as SystemPageSeoProps;
  if (!pageContent) return null;

  return (
    <SEO
      locale={pageContext.locale}
      title={pageContent.seoTitle}
      description={pageContent.seoDescription?.seoDescription}
      robots={pageContent.seoRobots?.seoRobots}
      alternates={pageContext.alternates}
      image={pageContent.openGraphImage?.url}
    />
  );
};

export default HomePage;
